import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { GetAthleteToken } from 'services/athlete-auth-service';

interface AthleteAuthState {
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AthleteCredentials {
  country: string;
  storeNumber: string;
  employeeId: string;
  password: string;
}

interface AthleteContextType {
  athleteAuthState: AthleteAuthState;
  login: (creds: AthleteCredentials) => Promise<boolean>;
  logout: () => void;
  isAthleteAuthenticated: () => boolean;
  clearError: () => void;
}

const AthleteContext = createContext<AthleteContextType | undefined>(undefined);

interface AthleteProviderProps {
  children: ReactNode;
}

export const AthleteProvider: React.FC<AthleteProviderProps> = ({ children }) => {
  const [athleteAuthState, setAthleteAuthState] = useState<AthleteAuthState>({
    accessToken: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  });

  // Check for existing token on provider initialization
  useEffect(() => {
    const existingToken = sessionStorage.getItem('athlete-token');
    if (existingToken && isTokenValid(existingToken)) {
      setAthleteAuthState(prev => ({
        ...prev,
        accessToken: existingToken,
        isAuthenticated: true,
      }));
    }
  }, []);

  const isTokenValid = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  const login = useCallback(async (creds: AthleteCredentials): Promise<boolean> => {
    setAthleteAuthState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const athleteTokenRes = await GetAthleteToken(creds);
      if (athleteTokenRes && athleteTokenRes.data.access_token) {
        setAthleteAuthState({
          accessToken: athleteTokenRes.data.access_token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        sessionStorage.setItem('athlete-token', athleteTokenRes.data.access_token);
        return true;
      } else {
        throw new Error('No access token received');
      }
    } catch (error) {
      setAthleteAuthState({
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication failed',
      });
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setAthleteAuthState({
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
    sessionStorage.removeItem('athlete-token');
  }, []);

  const isAthleteAuthenticated = useCallback((): boolean => {
    const token = sessionStorage.getItem('athlete-token');
    if (!token) return false;

    const isValid = isTokenValid(token);
    return isValid;
  }, [athleteAuthState.isAuthenticated]);

  const clearError = useCallback(() => {
    setAthleteAuthState(prev => ({
      ...prev,
      error: null,
    }));
  }, []);

  const contextValue: AthleteContextType = {
    athleteAuthState,
    login,
    logout,
    isAthleteAuthenticated,
    clearError,
  };

  return <AthleteContext.Provider value={contextValue}>{children}</AthleteContext.Provider>;
};

export const useAthleteContext = (): AthleteContextType => {
  const context = useContext(AthleteContext);
  if (!context) {
    throw new Error('useAthleteContext must be used within an AthleteProvider');
  }
  return context;
};
